import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

const ROLES = ["owner", "admin", "cashier", "waiter", "chef", "delivery-manager"] as const;

export const listMembersSkill: Skill = {
  id: "listMembers",
  description: "Lista los miembros del restaurante (id, nombre, email, rol).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando miembros…",
      successMsg: "✓ Miembros obtenidos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/members`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          const members = items.map((m) => ({
            id: m.id,
            name: m.user ? `${m.user.firstName} ${m.user.lastName}` : m.name,
            email: m.user?.email,
            role: m.role,
          }));
          return {
            ok: true as const,
            result: { count: members.length, members },
            humanSummary: `${members.length} miembro(s).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const inviteMemberSkill: Skill = {
  id: "inviteMember",
  description:
    "Envía una invitación por email para sumar a alguien al restaurante con un rol específico. Owner/admin solamente.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      invitedEmail: { type: "string" },
      role: { type: "string", enum: [...ROLES] },
    },
    required: ["restaurantId", "invitedEmail", "role"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const invitedEmail = String(args.invitedEmail ?? "").trim().toLowerCase();
    const role = String(args.role ?? "");
    if (!restaurantId || !invitedEmail || !role) return { ok: false, error: "Faltan datos." };
    if (!ROLES.includes(role as (typeof ROLES)[number])) return { ok: false, error: `Rol inválido: ${role}` };

    const ok = await ctx.confirmation.confirm({
      title: "Invitar miembro",
      description: "Se enviará un correo de invitación al destinatario.",
      details: [
        { label: "Email", value: invitedEmail },
        { label: "Rol", value: role },
      ],
      confirmLabel: "Enviar invitación",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Invitando a ${invitedEmail}…`,
      successMsg: "✓ Invitación enviada",
      restaurantId,
      targetTab: "invitaciones",
      targetTabLabel: "Invitaciones",
      run: async () => {
        try {
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/members/invite`,
            { invitedEmail, role },
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { id: data?.id, invitedEmail, role },
            humanSummary: `Invitación enviada a ${invitedEmail}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateMemberRoleSkill: Skill = {
  id: "updateMemberRole",
  description: "Cambia el rol de un miembro existente del restaurante.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      memberId: { type: "string" },
      role: { type: "string", enum: [...ROLES] },
    },
    required: ["restaurantId", "memberId", "role"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const memberId = String(args.memberId ?? "");
    const role = String(args.role ?? "");
    if (!restaurantId || !memberId || !role) return { ok: false, error: "Faltan datos." };

    const ok = await ctx.confirmation.confirm({
      title: "Cambiar rol",
      details: [{ label: "Nuevo rol", value: role }],
      confirmLabel: "Cambiar rol",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Cambiando rol a ${role}…`,
      successMsg: "✓ Rol actualizado",
      restaurantId,
      targetTab: "personal",
      targetTabLabel: "Personal",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/members/${memberId}/role`,
            { role },
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { memberId, role },
            humanSummary: `Rol cambiado a ${role}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const removeMemberSkill: Skill = {
  id: "removeMember",
  description: "Elimina un miembro del restaurante (revoca su acceso). IRREVERSIBLE.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, memberId: { type: "string" } },
    required: ["restaurantId", "memberId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const memberId = String(args.memberId ?? "");
    if (!restaurantId || !memberId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar miembro",
      description: "Se revocará el acceso de este miembro al restaurante.",
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Eliminando miembro…",
      successMsg: "✓ Miembro eliminado",
      restaurantId,
      targetTab: "personal",
      targetTabLabel: "Personal",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/members/${memberId}`,
            ctx.accessToken,
          );
          return { ok: true as const, result: { memberId, removed: true }, humanSummary: "Miembro eliminado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const listInvitationsSkill: Skill = {
  id: "listInvitations",
  description: "Lista las invitaciones pendientes/aceptadas del restaurante.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando invitaciones…",
      successMsg: "✓ Invitaciones obtenidas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/members/invitations`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              invitations: items.map((i) => ({
                id: i.id,
                email: i.invitedEmail,
                role: i.role,
                status: i.status,
              })),
            },
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const cancelInvitationSkill: Skill = {
  id: "cancelInvitation",
  description: "Cancela una invitación enviada que aún no fue aceptada.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, invitationId: { type: "string" } },
    required: ["restaurantId", "invitationId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const invitationId = String(args.invitationId ?? "");
    if (!restaurantId || !invitationId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Cancelar invitación",
      confirmLabel: "Cancelar invitación",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Cancelando invitación…",
      successMsg: "✓ Invitación cancelada",
      restaurantId,
      targetTab: "invitaciones",
      targetTabLabel: "Invitaciones",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/invitations/${invitationId}`,
            ctx.accessToken,
          );
          return { ok: true as const, result: { invitationId }, humanSummary: "Invitación cancelada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
